import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Onboarding.css';
/* .onboarding-exit-btn lives in OnboardingProgress.css; load it here so the
   skip pill looks the same whether or not another onboarding page loaded first. */
import '../components/OnboardingProgress.css';
import BrandLogo from '../components/BrandLogo';
import OnboardingAurora from '../components/OnboardingAurora';
import { Button } from '../components/ui/button';
import { Rise } from '../components/ui/motion';
import { onboardingAPI } from '../services/api';

const ONBOARDING_KEY = 'lanura_onboarding_complete';

/**
 * Cinematic single-screen welcome. The old four click-through panels are
 * merged into one staged reveal: headline → what happens → CTA. The CTA
 * drops the user straight into the context step (the /baseline-quiz start
 * screen still exists for people who land there directly).
 */
const Onboarding: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const redo = params.get('redo') === '1';
    const complete = localStorage.getItem(ONBOARDING_KEY) === 'true';
    if (complete && !redo) {
      navigate('/', { replace: true });
    }
  }, [navigate]);

  const handleSkip = () => {
    localStorage.setItem(ONBOARDING_KEY, 'true');
    navigate('/dashboard', { replace: true });
  };

  const handleStart = async () => {
    try {
      await onboardingAPI.updateMine({ onboarding: { progress_step: 'context' } });
    } catch {}
    navigate('/baseline-quiz/context');
  };

  // Each element animates directly on SPRING (no variant propagation, which
  // froze the headline at its hidden pose on hard reloads).

  return (
    <div className="onboarding onb-intro">
      <OnboardingAurora stage={1} />

      <div className="onb-intro-top">
        <span className="onb-intro-top-spacer" aria-hidden="true" />
        <div className="onb-intro-brand">
          <BrandLogo width={124} height={31} />
        </div>
        <button type="button" className="onboarding-exit-btn" onClick={handleSkip}>
          Skip for now
        </button>
      </div>

      <main className="onb-intro-main">
        <Rise as="div" index={0} className="onb-intro-eyebrow">
          Welcome to Lanura
        </Rise>

        <Rise as="h1" index={1} className="onb-intro-title">
          Let&rsquo;s build your
          <br />
          <span className="onb-intro-title-accent">communication profile.</span>
        </Rise>

        <Rise as="p" index={2} className="onb-intro-sub">
          A little context and one practice conversation - then coaching that&rsquo;s actually about
          how <em>you</em> communicate.
        </Rise>

        <Rise as="div" index={3} className="onb-intro-steps">
          <div className="onb-intro-step">
            <span className="onb-intro-step-num">01</span>
            <span className="onb-intro-step-title">Tell us about your work</span>
            <span className="onb-intro-step-desc">
              Role, area and what feels hardest - one minute.
            </span>
          </div>
          <div className="onb-intro-step">
            <span className="onb-intro-step-num">02</span>
            <span className="onb-intro-step-title">One real conversation</span>
            <span className="onb-intro-step-desc">
              Practise with a lifelike counterpart, picked for your role.
            </span>
          </div>
          <div className="onb-intro-step">
            <span className="onb-intro-step-num">03</span>
            <span className="onb-intro-step-title">Your first conversation profile</span>
            <span className="onb-intro-step-desc">
              Strengths, a development focus and a first focus area to start from.
            </span>
          </div>
        </Rise>

        <Rise as="div" index={4} className="onb-intro-cta-row">
          <Button size="lg" className="onb-intro-cta" onClick={() => void handleStart()}>
            Build my profile →
          </Button>
        </Rise>

        <Rise as="p" index={5} className="onb-intro-footnote">
          🔒 Private to you · About 10 minutes
        </Rise>
      </main>
    </div>
  );
};

export default Onboarding;
